import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../../services/authService';
import { listScripts } from '../../services/baiqlScriptService';

const ScriptShareDialogContent = ({ sharePrincipalType, setSharePrincipalType, setSharePrincipalId, userTeams, userOrgs }) => {
  const [isTeamOrOrgUser, setIsTeamOrOrgUser] = useState(false);
  
  useEffect(() => {
    (async () => {
      try {
        const user = await authService.getUserInfo();
        const roles = user?.roles || [];
        const isTeamOrOrg = roles.some(r => 
          ['teamuser', 'teamadmin', 'enterpriseuser', 'enterpriseadmin'].includes(String(r).toLowerCase())
        );
        setIsTeamOrOrgUser(isTeamOrOrg);
      } catch {
        setIsTeamOrOrgUser(false);
      }
    })();
  }, []);
  
  return (
    <>
      {isTeamOrOrgUser && (
        <div style={{ marginBottom: '12px', padding: '12px', background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: '8px', fontSize: '14px', color: '#92400e' }}>
          Team/Org users can only share scripts with their own teams/organizations, not with individual users.
        </div>
      )}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
        <label style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <span>Principal Type</span>
          <select value={sharePrincipalType} onChange={(e) => {
            setSharePrincipalType(e.target.value);
            setSharePrincipalId(''); // Reset when changing type
          }}>
            <option value="team">Team</option>
            <option value="org">Org</option>
            {!isTeamOrOrgUser && <option value="user">User</option>}
          </select>
        </label>
      </div>
    </>
  );
};

const ScriptsLibrary = () => {
  const [activeTab, setActiveTab] = useState('my');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareTargetId, setShareTargetId] = useState(null);
  const [sharePrincipalType, setSharePrincipalType] = useState('team');
  const [sharePrincipalId, setSharePrincipalId] = useState('');
  const [shareRole, setShareRole] = useState('viewer');
  const [shareExpiresAt, setShareExpiresAt] = useState('');
  const [recipientData, setRecipientData] = useState(null);
  const [userTeams, setUserTeams] = useState([]);
  const [userOrgs, setUserOrgs] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        if (activeTab === 'my') {
          // Use the same service the editor uses (known good)
          let data = await listScripts().catch(() => []);
          if (!Array.isArray(data) || data.length === 0) {
            // Fallback to legacy endpoint used by editor
            try {
              const t = authService.getToken && authService.getToken();
              const r = await fetch('/api/baiql/execute/scripts', { headers: t ? { Authorization: `Bearer ${t}` } : {}, credentials: 'include' });
              if (r.ok) {
                const alt = await r.json();
                if (Array.isArray(alt)) {
                  data = alt.map(x => ({ id: x.id || x.scriptId || x.Id, name: x.name || x.Name, modified: x.modified || x.modifiedAt || x.Modified || x.ModifiedAt }));
                }
              }
            } catch {}
          }
          setItems(Array.isArray(data) ? data : []);
        } else {
          const token = authService.getToken();
          let url = '/api/baiql/scripts';
          if (activeTab === 'team') url = '/api/baiql/scripts/team';
          if (activeTab === 'org') url = '/api/baiql/scripts/org';
          if (activeTab === 'public') url = '/api/baiql/scripts/community';
          const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
          const r = await fetch(url, { headers, credentials: 'include' });
          if (!r.ok) {
            setItems([]);
          } else {
            const data = await r.json();
            setItems(Array.isArray(data) ? data : []);
          }
        }
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [activeTab]);

  const showToast = (m, type='success') => setToast({ m, type });

  const openScript = (id) => {
    try {
      navigate(`/baiql?scriptId=${encodeURIComponent(id)}`);
    } catch {
      navigate(`/baiql`, { state: { scriptId: id } });
    }
  };

  const forkScript = async (id) => {
    try {
      const token = authService.getToken();
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const r = await fetch(`/api/baiql/scripts/${id}/fork`, { method: 'POST', headers, credentials: 'include' });
      if (!r.ok) throw new Error(await r.text());
      showToast('Saved to My Scripts');
    } catch {
      showToast('Failed to save', 'error');
    }
  };

  const deleteScript = async (id) => {
    try {
      const token = authService.getToken();
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const r = await fetch(`/api/baiql/scripts/${id}`, { method: 'DELETE', headers, credentials: 'include' });
      if (!r.ok) throw new Error(await r.text());
      setItems(prev => prev.filter(x => x.id !== id));
      showToast('Deleted');
    } catch {
      showToast('Failed to delete', 'error');
    }
  };

  const publish = async (id, makePublic) => {
    try {
      const token = authService.getToken();
      const endpoint = makePublic ? 'publish' : 'unpublish';
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const r = await fetch(`/api/baiql/scripts/${id}/${endpoint}`, { method: 'POST', headers, credentials: 'include' });
      if (!r.ok) throw new Error(await r.text());
      showToast(makePublic ? 'Published' : 'Unpublished');
    } catch {
      showToast('Failed to update visibility', 'error');
    }
  };

  const share = async () => {
    try {
      const token = authService.getToken();
      const body = {
        principalType: sharePrincipalType,
        principalId: sharePrincipalId,
        role: shareRole,
        expiresAt: shareExpiresAt ? new Date(shareExpiresAt).toISOString() : null
      };
      const headers = token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
      const r = await fetch(`/api/baiql/scripts/${shareTargetId}/share`, { method: 'POST', headers, credentials: 'include', body: JSON.stringify(body) });
      if (!r.ok) throw new Error(await r.text());
      setShareOpen(false);
      setSharePrincipalId('');
      showToast('Share updated');
    } catch {
      showToast('Failed to share', 'error');
    }
  };

  return (
    <div className="scripts-library">
      <div className="saved-prompts-header">
        <h1>Scripts Library</h1>
        <div className="tab-navigation">
          <button className={`tab-button ${activeTab === 'my' ? 'active' : ''}`} onClick={() => setActiveTab('my')}>My</button>
          <button className={`tab-button ${activeTab === 'team' ? 'active' : ''}`} onClick={() => setActiveTab('team')}>Team</button>
          <button className={`tab-button ${activeTab === 'org' ? 'active' : ''}`} onClick={() => setActiveTab('org')}>Org</button>
          <button className={`tab-button ${activeTab === 'public' ? 'active' : ''}`} onClick={() => setActiveTab('public')}>Public</button>
        </div>
      </div>

      {loading ? <div className="loading-spinner">Loading...</div> : (
        <div className="prompts-grid">
          {items.map(item => (
            <div className="prompt-card" key={item.id}>
              <div className="prompt-header">
                <div className="prompt-title">
                  <div className="topic-value">{item.name}</div>
                </div>
              </div>
              <div className="prompt-stats">
                <div className="stats-row">
                  <span className="stat-item">❤️ {item.likeCount ?? 0}</span>
                  <span className="stat-item">📊 {item.useCount ?? 0} uses</span>
                </div>
              </div>
              <div className="prompt-actions" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                <button className="action-button primary" onClick={() => openScript(item.id)}>Open</button>
                {activeTab !== 'my' ? (
                  <button className="action-button secondary" onClick={() => forkScript(item.id)}>Save to My Scripts</button>
                ) : (
                  <button className="action-button" onClick={async () => {
                    setShareTargetId(item.id);
                    try {
                      const ms = 30 * 24 * 60 * 60 * 1000;
                      const d = new Date(Date.now() + ms);
                      const pad = (n) => String(n).toString().padStart(2,'0');
                      const local = `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
                      setShareExpiresAt(local);
                      
                      // Get user's teams/orgs for sharing dropdown
                      const token = authService.getToken();
                      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
                      
                      // Get user's teams and orgs for sharing dropdown
                      const [teamsRes, orgsRes] = await Promise.all([
                        fetch('/api/teams/mine', { headers, credentials: 'include' }),
                        fetch('/api/orgs/mine', { headers, credentials: 'include' })
                      ]);
                      
                      if (teamsRes.ok) {
                        const teams = await teamsRes.json();
                        setUserTeams(Array.isArray(teams) ? teams : []);
                      }
                      if (orgsRes.ok) {
                        const orgs = await orgsRes.json();
                        setUserOrgs(Array.isArray(orgs) ? orgs : []);
                      }
                    } catch {}
                    setShareOpen(true);
                  }}>Share</button>
                )}
                {activeTab === 'my' ? (
                  <button
                    className="action-button"
                    onClick={async () => {
                      try {
                        const makePublic = !Boolean(item.isPublic);
                        await publish(item.id, makePublic);
                        setItems(prev => prev.map(x => x.id === item.id ? { ...x, isPublic: makePublic } : x));
                      } catch {}
                    }}
                  >
                    {item?.isPublic ? 'Make Private' : 'Make Public'}
                  </button>
                ) : (
                  <span />
                )}
                {activeTab === 'my' ? (
                  <button className="action-button danger" onClick={() => deleteScript(item.id)}>Delete</button>
                ) : (
                  <span />
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {shareOpen && (
        <div className="help-modal-overlay" onClick={() => setShareOpen(false)}>
          <div className="help-modal" style={{ maxWidth: '520px' }} onClick={(e) => e.stopPropagation()}>
            <div className="help-modal-header">
              <h2>Share Script</h2>
              <button onClick={() => setShareOpen(false)} className="help-modal-close" title="Close">&times;</button>
            </div>
            <div className="help-modal-content" style={{ padding: '1.5rem' }}>
              <ScriptShareDialogContent 
                sharePrincipalType={sharePrincipalType}
                setSharePrincipalType={setSharePrincipalType}
                setSharePrincipalId={setSharePrincipalId}
                userTeams={userTeams}
                userOrgs={userOrgs}
              />
              <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
                <label style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  <span>Role</span>
                  <select value={shareRole} onChange={(e) => setShareRole(e.target.value)}>
                    <option value="viewer">Viewer</option>
                    <option value="editor">Editor</option>
                  </select>
                </label>
              </div>
              {sharePrincipalType === 'user' ? (
                <>
                  {(() => {
                    const hasLoaded = !!recipientData;
                    const loadRecipients = async () => {
                      if (hasLoaded) return;
                      try {
                        const t = authService.getToken && authService.getToken();
                        const r = await fetch('/api/shares/recipients', { headers: t ? { Authorization: `Bearer ${t}` } : {}, credentials: 'include' });
                        if (r.ok) {
                          const j = await r.json();
                          setRecipientData(j);
                        } else {
                          setRecipientData({});
                        }
                      } catch { setRecipientData({}); }
                    };
                    if (!hasLoaded) { void loadRecipients(); }
                    const groups = [];
                    if (Array.isArray(recipientData?.admins) && recipientData.admins.length) {
                      groups.push({ label: 'Admins', members: recipientData.admins });
                    }
                    if (Array.isArray(recipientData?.teams)) {
                      for (const t of recipientData.teams) {
                        if (Array.isArray(t.members) && t.members.length) {
                          groups.push({ label: `Team: ${t.name}`, members: t.members });
                        }
                      }
                    }
                    if (Array.isArray(recipientData?.orgs)) {
                      for (const o of recipientData.orgs) {
                        if (Array.isArray(o.members) && o.members.length) {
                          groups.push({ label: `Org: ${o.name}`, members: o.members });
                        }
                      }
                    }
                    const hasChoices = groups.some(g => g.members.length > 0);
                    if (hasLoaded && hasChoices) {
                      return (
                        <label style={{ display: 'flex', flexDirection: 'column', marginBottom: '12px' }}>
                          <span>User (Team/Org member)</span>
                          <select value={sharePrincipalId} onChange={(e)=>setSharePrincipalId(e.target.value)}>
                            <option value="">Select recipient…</option>
                            {groups.map((g, gi) => (
                              <optgroup key={gi} label={g.label}>
                                {g.members.map((m, mi) => (
                                  <option key={mi} value={m.userId}>{m.userName || m.email || m.userId}</option>
                                ))}
                              </optgroup>
                            ))}
                          </select>
                        </label>
                      );
                    }
                    return (
                      <label style={{ display: 'flex', flexDirection: 'column', marginBottom: '12px' }}>
                        <span>User Id</span>
                        <input value={sharePrincipalId} onChange={(e) => setSharePrincipalId(e.target.value)} placeholder="user id" />
                        {!hasLoaded && <small style={{ color:'#6b7280' }}>Loading recipients…</small>}
                        {hasLoaded && !hasChoices && <small style={{ color:'#6b7280' }}>No team/org members found; enter user ID manually.</small>}
                      </label>
                    );
                  })()}
                </>
              ) : (
                <label style={{ display: 'flex', flexDirection: 'column', marginBottom: '12px' }}>
                  <span>{sharePrincipalType === 'team' ? 'Team' : 'Organization'}</span>
                  {sharePrincipalType === 'team' ? (
                    <select value={sharePrincipalId} onChange={(e) => setSharePrincipalId(e.target.value)}>
                      <option value="">Select team...</option>
                      {userTeams.map(team => (
                        <option key={team.id} value={team.id}>{team.name}</option>
                      ))}
                    </select>
                  ) : (
                    <select value={sharePrincipalId} onChange={(e) => setSharePrincipalId(e.target.value)}>
                      <option value="">Select organization...</option>
                      {userOrgs.map(org => (
                        <option key={org.id} value={org.id}>{org.name}</option>
                      ))}
                    </select>
                  )}
                  {sharePrincipalType === 'team' && userTeams.length === 0 && (
                    <small style={{ color:'#6b7280', marginTop: '4px' }}>You are not a member of any teams.</small>
                  )}
                  {sharePrincipalType === 'org' && userOrgs.length === 0 && (
                    <small style={{ color:'#6b7280', marginTop: '4px' }}>You are not a member of any organizations.</small>
                  )}
                </label>
              )}
              <label style={{ display: 'flex', flexDirection: 'column' }}>
                <span>Expires At (optional)</span>
                <input type="datetime-local" value={shareExpiresAt} onChange={(e) => setShareExpiresAt(e.target.value)} />
              </label>
            </div>
            <div className="help-modal-footer" style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', padding: '1rem' }}>
              <button onClick={() => setShareOpen(false)} className="help-modal-btn help-modal-btn-cancel">Cancel</button>
              <button onClick={share} className="help-modal-btn help-modal-btn-primary">Save</button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={`toast ${toast.type}`} onClick={() => setToast(null)} style={{ position: 'fixed', right: 16, bottom: 16, background: '#333', color: '#fff', padding: '10px 14px', borderRadius: 6 }}>
          {toast.m}
        </div>
      )}
    </div>
  );
};

export default ScriptsLibrary;


